import clsx from 'clsx'
import { useEffect, useRef, useState, type RefObject } from 'react'

import { CaseLawGavel, Document, LawsRegs, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import TruncatedText from '@/components/truncated-text/TruncatedText'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { ViewerDocumentKind, type ContentViewerSession } from '@/content-viewer/store/schemas'
import { useAppDispatch } from '@/store/store-hooks'
import { useChatPanelSourceLabel } from '../hooks/useChatPanelSourceLabel'

const kFadeOutMs = 150
const kCollapseMs = 200

type SourceChipProps = {
  conversationId: string
  /* The source open in the collapsed chat panel. */
  sourceSession: ContentViewerSession
  /* Opens the chat panel on the source's tab. */
  onOpen: () => void
  /* Focused when the source is closed, since the chip unmounts with it. */
  focusAfterCloseRef?: RefObject<HTMLButtonElement>
}

/** Stands in for the source tab while the chat panel is collapsed, so the open source stays reachable. */
export default function SourceChip({ conversationId, sourceSession, onOpen, focusAfterCloseRef }: SourceChipProps) {
  const dispatch = useAppDispatch()
  const { title, label } = useChatPanelSourceLabel({ conversationId, sourceSession })
  const Icon = getSourceIcon(sourceSession.document.kind)
  const [isClosing, setIsClosing] = useState(false)

  // Dropped if the chip goes before the source does, so a source opened in the meantime is not closed in its place.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(closeTimerRef.current), [])

  // The chip fades out and slides shut before the source closes and unmounts it.
  const closeSource = () => {
    setIsClosing(true)
    focusAfterCloseRef?.current?.focus()
    closeTimerRef.current = setTimeout(() => dispatch(ContentViewerActions.clearContentViewer()), kCollapseMs)
  }

  return (
    // Shrinks its grid column to nothing while closing, so whatever sits beside the chip slides into its place.
    <div
      className={clsx('grid transition-[grid-template-columns] ease-out motion-reduce:transition-none', isClosing ? 'grid-cols-[0fr]' : 'grid-cols-[1fr]')}
      style={{ transitionDuration: `${kCollapseMs}ms` }}
    >
      {/* Clips only while closing, so the chip's shadow shows the rest of the time. */}
      <div className={clsx('min-w-0', isClosing && 'overflow-hidden')}>
        {/* The space before the chip sits inside the shrinking column, so it closes up with the chip. Spacing outside
            the column would stay behind, leaving whatever the chip slides away from to jump the last few pixels. */}
        <div className="pl-3">
          <div
            className={clsx(
              // Keeps its own width as the column shrinks, so the title slides out of view rather than truncating further.
              'flex h-9 w-max max-w-60 items-center gap-1 rounded-md border border-brand-neutral-200 bg-brand-neutral-50 pr-0.5 shadow hover:bg-brand-neutral-100',
              'transition-opacity ease-out motion-reduce:transition-none',
              isClosing && 'pointer-events-none opacity-0'
            )}
            style={{ transitionDuration: `${kFadeOutMs}ms` }}
          >
            <button type="button" onClick={onOpen} className="flex h-full min-w-0 items-center gap-1.5 rounded-md pl-2">
              <Icon className="size-5 shrink-0 text-brand-neutral-800" />
              <TruncatedText className="text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-800" disableOnTouch>
                {label}
              </TruncatedText>
            </button>
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              icon={<XClose />}
              aria-label={`Close ${title}`}
              onClick={closeSource}
              wrapperClassName="shrink-0"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function getSourceIcon(kind: ViewerDocumentKind) {
  switch (kind) {
    case ViewerDocumentKind.CASELAW:
      return CaseLawGavel
    case ViewerDocumentKind.LRR:
      return LawsRegs
    default:
      return Document
  }
}
